<template>
    <redesign-base-chart
        class="w-full h-full"
        :widget="data"
        :option="chartOptions"
    />
</template>

<script setup lang="ts">
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed, toRef } from 'vue'
import { ExtensionStatusData, getExtensionStatusData } from '@/composables/useExtensionStatus'
import redesignStatusTypes, { Statuses } from '@/enum/redesignStatusTypes'
import { useExtensions } from '@/composables/useExtensions'
import i18n from '@/i18n'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { currentAccountEntitiesExtensions } from '@/composables/globalData/useEntitiesData'

interface PieChartItem {
    name: string
    value: number
    selected: boolean
    itemStyle?: { color: string }
}

const props = defineProps<{
    data: WidgetExtended
    fontSize: number
    labelsColors: string
}>()

const widgetData = toRef(props, 'data')
const holdStatusLabel = i18n.t(redesignStatusTypes.value[Statuses.HOLD].text)
const { extensions } = useExtensions()

const filteredExtensions = computed(() => {
    return extensions.value.filter(({ number }) => !!currentAccountEntitiesExtensions.value[number])
})

const { filteredItems } = useEntityPositiveNegativeFilters(widgetData, '{|User_ID_id_list|}', filteredExtensions, 'userID')

//Computed
const hideLoggedOutUsers = computed(() => {
    return props.data.WidgetLayout.hideLoggedOff
})

const pieChartData = computed(() => {
    const result = filteredItems.value.reduce<Record<string, PieChartItem>>((acc, extension, index) => {
        if(hideLoggedOutUsers.value && extension.representativeStatus === Statuses.LOGOUT) {
            return acc
        }
        let statusData: ExtensionStatusData = {
            text: holdStatusLabel as string,
            iconName: redesignStatusTypes.value[Statuses.HOLD].icon,
            color: redesignStatusTypes.value[Statuses.HOLD].color

        }
        if (extension.representativeStatus !== Statuses.HOLD) {
            statusData = getExtensionStatusData(extension, {})
        }

        const statusText = statusData?.text
        const statusColor = statusData?.color

        if (!statusText) return acc

        if (!acc[statusText]) {
            acc[statusText] = {
                name: statusText,
                selected: index === 0,
                value: 1,
                itemStyle: statusColor ? { color: statusColor } : undefined
            }
        } else {
            acc[statusText].value += 1
        }

        return acc
    }, {})

    return Object.values(result)
})


const chartOptions = computed(() => {
    return {
        tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)'
        },
        avoidLabelOverlap: false,
        legend: {
            show: false
        },
        series: [
            {
                name: '',
                type: 'pie',
                minAngle: 1,
                padAngle: 1,
                label: {
                    show: true,
                    formatter: '{b}: {d}%',
                    color: props.labelsColors,
                    fontSize: props.fontSize
                },
                radius: '75%',
                data: pieChartData.value,
                selectedMode: true,
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }
        ]
    }

})

</script>
<style scoped lang="scss">

</style>
